import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MOCK_PROJECTS, MOCK_ORGANIZATIONS, MOCK_DONATIONS } from "../lib/mockData";
import { DonationForm } from "../components/donations/DonationForm";
import { HumaTekFooter } from "../components/layout/HumaTekFooter";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { formatCurrency, getStatusColor, formatDateTime } from "../lib/utils";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Globe,
  Heart,
  MapPin,
  Target,
  Users,
} from "lucide-react";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const project = MOCK_PROJECTS.find((p) => p.id === id);
  const organization = MOCK_ORGANIZATIONS.find((o) => o.id === project?.organization_id);
  const projectDonations = MOCK_DONATIONS.filter((d) => d.project_id === id).slice(0, 5);

  if (!project) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F4F5F7]">
        <div className="text-center">
          <h2 className="font-heading font-bold text-2xl text-slate-800 mb-2">Project Not Found</h2>
          <p className="text-slate-500 mb-4">This project may have been removed or doesn't exist.</p>
          <Button asChild><Link to="/projects">Browse Projects</Link></Button>
        </div>
      </div>
    );
  }

  const progress = (project.current_amount / project.target_amount) * 100;
  const milestonesDone = project.milestones?.filter((m) => m.status === "completed").length || 0;
  const milestonesTotal = project.milestones?.length || 0;

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 bg-[#F4F5F7]" data-testid="project-detail-page">
      <div className="container mx-auto max-w-6xl">
        <Button variant="ghost" asChild className="mb-6"><Link to="/projects"><ArrowLeft className="h-4 w-4 mr-2" />Back to Projects</Link></Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h1 className="font-heading font-bold text-2xl text-slate-800 mb-2" data-testid="project-title">{project.title}</h1>
                      {organization && (
                        <div className="text-slate-500 flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {organization.name}
                          {organization.verified && <Badge className="bg-green-500/20 text-green-500 text-xs">Verified</Badge>}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className={`text-sm ${getStatusColor(project.status)}`}>{project.status?.replace("_", " ")}</Badge>
                  </div>

                  <p className="text-slate-600 leading-relaxed mb-6">{project.description}</p>

                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-2">
                      <div><p className="text-2xl font-heading font-bold text-primary">{formatCurrency(project.current_amount)}</p><p className="text-sm text-slate-400">raised of {formatCurrency(project.target_amount)} goal</p></div>
                      <p className="text-lg font-bold text-slate-800">{progress.toFixed(0)}%</p>
                    </div>
                    <Progress value={progress} className="h-3" />
                  </div>

                  {/* Key Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-slate-50 text-center"><MapPin className="h-4 w-4 text-primary mx-auto mb-1" /><p className="text-sm font-medium text-slate-800">{project.country}</p><p className="text-xs text-slate-400">Location</p></div>
                    <div className="p-3 rounded-lg bg-slate-50 text-center"><Users className="h-4 w-4 text-primary mx-auto mb-1" /><p className="text-sm font-medium text-slate-800">{(project.beneficiary_count || 0).toLocaleString()}</p><p className="text-xs text-slate-400">Beneficiaries</p></div>
                    <div className="p-3 rounded-lg bg-slate-50 text-center"><Target className="h-4 w-4 text-primary mx-auto mb-1" /><p className="text-sm font-medium text-slate-800">{milestonesDone}/{milestonesTotal}</p><p className="text-xs text-slate-400">Milestones</p></div>
                    <div className="p-3 rounded-lg bg-slate-50 text-center"><Calendar className="h-4 w-4 text-primary mx-auto mb-1" /><p className="text-sm font-medium text-slate-800">{project.end_date ? new Date(project.end_date).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "TBD"}</p><p className="text-xs text-slate-400">Target Date</p></div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Milestones */}
            {project.milestones && project.milestones.length > 0 && (
              <Card className="bg-white">
                <CardHeader><CardTitle className="font-heading">Project Milestones</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {project.milestones.map((milestone, index) => (
                      <div key={milestone.id} className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 border border-slate-100" data-testid={`milestone-${index}`}>
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${milestone.status === "completed" ? "bg-green-100" : milestone.status === "in_progress" ? "bg-primary/10" : "bg-slate-200"}`}>
                          {milestone.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : milestone.status === "in_progress" ? <Clock className="h-4 w-4 text-primary" /> : <Clock className="h-4 w-4 text-slate-400" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-slate-800">{milestone.title}</p>
                            <Badge variant="outline" className={`text-xs ${milestone.status === "completed" ? "bg-green-500/20 text-green-600 border-green-200" : milestone.status === "in_progress" ? "bg-primary/10 text-primary border-primary/20" : ""}`}>{milestone.status?.replace("_", " ")}</Badge>
                          </div>
                          {milestone.description && <p className="text-sm text-slate-500 mt-1">{milestone.description}</p>}
                          {milestone.budget && <p className="text-xs text-slate-400 mt-1">Budget: {formatCurrency(milestone.budget)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Donations */}
            {projectDonations.length > 0 && (
              <Card className="bg-white">
                <CardHeader><CardTitle className="font-heading">Recent Donations</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {projectDonations.map((donation) => (
                      <div key={donation.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center"><Heart className="h-4 w-4 text-primary" /></div>
                          <div><p className="text-sm font-medium text-slate-800">{donation.is_anonymous ? "Anonymous" : donation.donor_name}</p><p className="text-xs text-slate-400">{formatDateTime(donation.created_at)}</p></div>
                        </div>
                        <p className="font-medium text-primary">{formatCurrency(donation.amount)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar — Donation Form */}
          <div className="lg:col-span-1 space-y-6">
            <DonationForm project={project} />
          </div>
        </div>
      </div>
      <div className="mt-12"><HumaTekFooter /></div>
    </div>
  );
}
